import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedDate } from 'react-intl';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { WithChaseHeaderLayout_so5UserGroup } from './__generated__/index.graphql';

const Header = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--intermediate-unit) 0;

  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

type Props = {
  title?: ReactNode;
  iconUrl?: Nullable<string>;
  so5UserGroup: WithChaseHeaderLayout_so5UserGroup;
  children: ReactNode;
  backTo: To;
  action?: ReactNode;
};

export const WithChaseHeaderLayout = ({
  title,
  iconUrl,
  so5UserGroup,
  children,
  backTo,
  action,
}: Props) => {
  return (
    <Container>
      <ContentWithStickyHeader
        header={
          <Header>
            <Horizontal gap={2}>
              <IconButton
                size="medium"
                color="transparent"
                icon={faTimes}
                to={backTo}
              />
              <Horizontal>
                {!!iconUrl && (
                  <img src={iconUrl} alt="" width="32" height="32" />
                )}
                <Vertical gap={0}>
                  <LabelL as="h1" bold>
                    {title || so5UserGroup.displayName}
                  </LabelL>
                  {!!so5UserGroup.startSo5Fixture && (
                    <LabelM as="p" color="var(--c-nd-600)">
                      {so5UserGroup.endSo5Fixture?.endDate ? (
                        <DateRange
                          startDate={so5UserGroup.startSo5Fixture.startDate}
                          endDate={so5UserGroup.endSo5Fixture.endDate}
                        />
                      ) : (
                        <FormattedDate
                          value={so5UserGroup.startSo5Fixture.startDate}
                          day="2-digit"
                          month="short"
                        />
                      )}
                    </LabelM>
                  )}
                </Vertical>
              </Horizontal>
            </Horizontal>
            {!!action && <div>{action}</div>}
          </Header>
        }
      >
        {children}
      </ContentWithStickyHeader>
    </Container>
  );
};

WithChaseHeaderLayout.fragments = {
  so5UserGroup: gql`
    fragment WithChaseHeaderLayout_so5UserGroup on So5UserGroup {
      slug
      displayName
      startSo5Fixture {
        slug
        startDate
      }
      endSo5Fixture {
        slug
        endDate
      }
    }
  ` as TypedDocumentNode<WithChaseHeaderLayout_so5UserGroup>,
};
